/**
 * Renders a PDF into canvases with pdf.js (pdfjs-dist, user decision 2026-10-10). The browser's own PDF viewer in a
 * frame cannot be printed alone from the page (Chrome printed the whole web page), so the report page shows and prints
 * these canvases instead. Loaded on demand: the library and its worker only reach the browser on the report page.
 */
export type PdfPages = {
  pages: HTMLCanvasElement[];
  /** size of the first page in PDF points (1/72 in), for the print page size */
  width: number;
  height: number;
};

/** Pixels per PDF point: 2 = 144 dpi, sharp on screen and good enough on paper. */
const SCALE = 2;

export async function renderPdfPages(data: ArrayBuffer): Promise<PdfPages> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const doc = await pdfjs.getDocument({ data }).promise;
  try {
    const pages: HTMLCanvasElement[] = [];
    let width = 0;
    let height = 0;
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      if (n === 1) {
        width = base.width;
        height = base.height;
      }
      const viewport = page.getViewport({ scale: SCALE });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.className = 'report-page';
      canvas.setAttribute('aria-label', `${n} / ${doc.numPages}`);
      await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
      pages.push(canvas);
    }
    return { pages, width, height };
  } finally {
    void doc.destroy();
  }
}
